import React from "react";
import { useNavigate } from "react-router-dom";
import {
  FaStore,
  FaHome,
  FaUser,
  FaCheckCircle,
  FaArrowRight,
  FaShieldAlt,
  FaClock,
  FaUnlock,
  FaSyncAlt,
  FaHeart,
} from "react-icons/fa";
import CustomerHeader from "../../components/CustomerHeader";
import CustomerFooter from "../../components/CustomerFooter";
import "./SignupSelection.css";

// "Join OGESTYLE" — premium role picker, styled to match the /for-business
// marketing page (cream + navy, Fraunces display, DM Sans body). Every card
// keeps its existing signup destination.

const IMG = {
  freelance: "/landing/freelance.jpg",
  owner: "/landing/business-signup.png",
};

const trust = [
  { icon: <FaUnlock />, label: "No contract — cancel anytime" },
  { icon: <FaClock />, label: "Registration in under 30 minutes" },
  { icon: <FaSyncAlt />, label: "Full 14-day free trial" },
  { icon: <FaShieldAlt />, label: "Payments secured by Stripe" },
];

const ownerChecks = [
  "Own or operate a salon, spa, barbershop or studio",
  "Add your whole team of professionals",
  "Manage multiple locations & staff",
  "Bookings, products & payments in one place",
];

const freelancerChecks = [
  "Independent, home-based or travel-to-client",
  "Your own dashboard with metrics & insights",
  "Discoverable profile & looks",
  "Email & SMS reminders included",
];

const customerChecks = [
  "Book beauty & wellness services near you",
  "No app to download — book from any device",
  "Save favorites, reviews & reminders",
];

function SignupSelection() {
  const navigate = useNavigate();

  return (
    <>
      <CustomerHeader />
      <main className="ss-page">
        {/* Hero */}
        <section className="ss-hero">
          <div className="ss-wrap ss-hero-center">
            <p className="ss-eyebrow">Join OGESTYLE</p>
            <h1 className="ss-display">Create your free account</h1>
            <p className="ss-sub">
              Start a full 14-day free trial — no contract, cancel anytime.
              Choose the account that fits how you work.
            </p>
          </div>
        </section>

        {/* Trust chips */}
        <section className="ss-wrap ss-trust" aria-label="Platform guarantees">
          {trust.map((t) => (
            <span key={t.label}>{t.icon} {t.label}</span>
          ))}
        </section>

        {/* Account choices */}
        <section className="ss-wrap ss-choose">
          {/* Business Owner (navy, image) */}
          <article className="ss-panel ss-panel--navy ss-panel--photo">
            <div className="ss-panel-img">
              <img
                src={IMG.owner}
                alt="A therapist providing a relaxing spa treatment in a bright studio"
              />
            </div>
            <div className="ss-panel-body">
              <span className="ss-panel-ic"><FaStore /></span>
              <p className="ss-panel-tag">Salon &amp; studio owners</p>
              <h2 className="ss-panel-title">Business Owner</h2>
              <p className="ss-panel-price">
                $50<span className="ss-panel-per">/month</span>
              </p>
              <p className="ss-panel-desc">
                One clear rate for your business — every professional working in
                your shop is included.
              </p>
              <ul className="ss-checks">
                {ownerChecks.map((c) => (
                  <li key={c}><FaCheckCircle /> {c}</li>
                ))}
              </ul>
              <button
                type="button"
                className="ss-btn ss-btn-light"
                onClick={() => navigate("/register-business")}
              >
                Register your business <FaArrowRight />
              </button>
            </div>
          </article>

          {/* Freelancer (cream, image) */}
          <article className="ss-panel ss-panel--cream">
            <div className="ss-panel-img">
              <img
                src={IMG.freelance}
                alt="An independent beauty professional at work"
              />
            </div>
            <div className="ss-panel-body">
              <span className="ss-panel-ic"><FaHome /></span>
              <p className="ss-panel-tag">Independent professionals</p>
              <h2 className="ss-panel-title">Freelancer</h2>
              <p className="ss-panel-price">
                $25<span className="ss-panel-per">/month</span>
              </p>
              <p className="ss-panel-desc">
                For home-based and travel-to-client beauty professionals — no
                per-seat fees.
              </p>
              <ul className="ss-checks">
                {freelancerChecks.map((c) => (
                  <li key={c}><FaCheckCircle /> {c}</li>
                ))}
              </ul>
              <button
                type="button"
                className="ss-btn ss-btn-primary"
                onClick={() => navigate("/freelancer/signup")}
              >
                Start as a freelancer <FaArrowRight />
              </button>
            </div>
          </article>

          {/* Customer */}
          <article className="ss-panel ss-panel--customer">
            <div className="ss-customer-row">
              <span className="ss-panel-ic"><FaUser /></span>
              <div className="ss-customer-copy">
                <p className="ss-panel-tag">Just here to book</p>
                <h2 className="ss-customer-title">Customer</h2>
                <ul className="ss-checks ss-checks--inline">
                  {customerChecks.map((c) => (
                    <li key={c}><FaCheckCircle /> {c}</li>
                  ))}
                </ul>
              </div>
              <div className="ss-customer-cta">
                <p className="ss-customer-free">
                  <FaHeart aria-hidden="true" /> Free
                </p>
                <button
                  type="button"
                  className="ss-btn ss-btn-outline"
                  onClick={() => navigate("/customer-signup")}
                >
                  Create customer account
                </button>
              </div>
            </div>
          </article>
        </section>

        {/* Login prompt */}
        <section className="ss-wrap ss-footer">
          <p>
            Already have an account?{" "}
            <span
              className="ss-login-link"
              onClick={() => navigate("/login")}
              role="link"
              tabIndex={0}
              onKeyDown={(e) => e.key === "Enter" && navigate("/login")}
            >
              Log in
            </span>
          </p>
          <p className="ss-support">
            Questions? Our team is happy to help —{" "}
            <a href="mailto:support@mg.ogestyle.com" className="ss-support-link">
              support@mg.ogestyle.com
            </a>
          </p>
        </section>
      </main>
      <CustomerFooter />
    </>
  );
}

export default SignupSelection;
